import React, { useMemo } from "react";
import { useStudent } from "../../DataStore/FeesStudentContext";
import ActivityLoader from "../Common/ActivityLoader";
import Notification from "../Common/Notification";
import StudentMgtHead from "./StudentMgtHead";
import { useTheme } from "../../DataStore/ThemeContext";
import { useNavigate } from "react-router-dom";
import { useFeesProfile } from "../../DataStore/FeesProfileContext";
import TextAvatar from "../Common/TextAvatar";

const StudentsCard = () => {
  const { studentList, searchText, selectedIds, setSelectedIds } = useStudent();
  const { openSettings } = useTheme();
  const { dispatch } = useFeesProfile();
  const navigate = useNavigate();

  const filteredStudents = useMemo(() => {
    if (!Array.isArray(studentList)) return [];
    const q = String(searchText || "").toLowerCase();
    if (!q) return studentList;
    return studentList.filter(
      (s) =>
        String(s.full_name || "").toLowerCase().includes(q) ||
        String(s.reg_no || "").toLowerCase().includes(q) ||
        String(s.payment_code || "").toLowerCase().includes(q)
    );
  }, [studentList, searchText]);

  const toggleSelection = (id) => {
    setSelectedIds(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id]
    );
  };

  const isSelected = (id) => selectedIds.includes(id);

  const allChecked =
    filteredStudents.length > 0 &&
    filteredStudents.every((s) => selectedIds.includes(s.id));

  const someChecked =
    filteredStudents.length > 0 &&
    filteredStudents.some((s) => selectedIds.includes(s.id)) &&
    !allChecked;

  const handleFeesGroup = (feesGroup) => {
    openSettings("Fees Structure", "FeesStructure", feesGroup, null, false);
  };

  const handleStudent = (id) => {
    dispatch({ type: "studentId", payload: id });
    navigate("/school-fees-pstudent");
  };

  return (
    <div className="card yx-studentsCard">

      <StudentMgtHead />

      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table yx-table mb-0">
            <thead className="yx-thead">
              <tr>
                <th className="yx-th yx-th--check">
                  <label className="yx-check">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = someChecked;
                      }}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(filteredStudents.map((s) => s.id));
                        } else {
                          setSelectedIds([]);
                        }
                      }}
                    />
                    <span className="yx-check__box" />
                  </label>
                </th>

                <th className="yx-th">Student</th>
                <th className="yx-th">Reg No.</th>
                <th className="yx-th">Gender</th>
                <th className="yx-th">Class</th>
                <th className="yx-th">Type</th>
                <th className="yx-th">Fees Group</th>
                <th className="yx-th">Payment Code</th>
                <th className="yx-th">Residence</th>
                <th className="yx-th">Home Contact</th>
              </tr>
            </thead>

            <tbody>
              {filteredStudents.map((student) => (
                <tr
                  key={student.id}
                  className={`yx-tr ${isSelected(student.id) ? "is-selected" : ""}`}
                  onClick={(e) => {
                    // avoid toggling when clicking links/buttons
                    const tag = (e.target?.tagName || "").toLowerCase();
                    if (tag === "a" || tag === "button") return;
                  }}
                >
                  <td className="yx-td yx-td--check" onClick={(e) => e.stopPropagation()}>
                    <label className="yx-check">
                      <input
                        type="checkbox"
                        checked={isSelected(student.id)}
                        onChange={() => toggleSelection(student.id)}
                      />
                      <span className="yx-check__box" />
                    </label>
                  </td>

                  <td className="yx-td">
                    <div className="d-flex align-items-center" style={{ gap: 12, minWidth: 260 }}>
                      <TextAvatar
                        text={student.initials}
                        size={42}
                        fontSize={13}
                        variant={isSelected(student.id) ? "solid" : "indigo"}
                      />

                      <div style={{ minWidth: 0 }}>
                        <div
                          className="yx-stuName"
                          onClick={() => handleStudent(student.id)}
                          title={student.full_name}
                        >
                          {student.full_name}
                        </div>
                        <div className="yx-stuSub">{student.reg_no}</div>
                      </div>
                    </div>
                  </td>

                  <td className="yx-td yx-mono">{student.reg_no}</td>
                  <td className="yx-td">{student.gender}</td>
                  <td className="yx-td">{student.class?.class_name || ""}</td>

                  <td className="yx-td">
                    <span className={`yx-pillMini ${student.flag === "new" ? "is-new" : "is-cont"}`}>
                      {student.flag === "new" ? "New student" : "Continuing"}
                    </span>
                  </td>

                  <td className="yx-td">
                    <span
                      className="yx-linkLike"
                      onClick={() => handleFeesGroup(student.fees_group)}
                      title={student.fees_group?.group_name || ""}
                    >
                      {student.fees_group?.group_name || "—"}
                    </span>
                  </td>

                  <td className="yx-td yx-mono">{student.payment_code}</td>
                  <td className="yx-td">{student.residence?.residence_name || ""}</td>
                  <td className="yx-td yx-mono">{student.home_contact}</td>
                </tr>
              ))}

              {filteredStudents.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center yx-muted py-4">
                    <Notification message="No students found." closable={false} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {!studentList && (
            <div className="p-3">
              <ActivityLoader />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
  

export default StudentsCard;
